import type {
  Conversation,
  ConversationId,
  ConversationMemberRef,
  Cursor,
  Message,
  MessageDraft,
  MessageReminder,
  Page,
  Reaction,
  SavedMessage,
  TimelineResponse,
  User,
  UserPreferences,
  UserRef,
  Uuid,
} from '@cyphex/types';
import type { ChatApi } from './contract';
import { apiFetch, query } from './request';

/** `{items, nextCursor}` → the `Page` shape every list hook consumes. */
function toPage<T>(items: T[], nextCursor: Cursor | null, cursor: Cursor | undefined): Page<T> {
  return {
    items,
    pageInfo: {
      hasNextPage: nextCursor !== null,
      hasPreviousPage: cursor !== undefined,
      startCursor: cursor ?? null,
      endCursor: nextCursor,
    },
  };
}

const emoji = (value: string): string => encodeURIComponent(value);
/** The members route's own maximum page. */
const MEMBER_PAGE_SIZE = 200;
/** A ceiling on the walk, so a broken cursor cannot loop for ever. */
const MEMBER_PAGES = 25;
const conv = (id: ConversationId): string => `/conversations/${id}`;

/**
 * Live implementation of `ChatApi` against apps/api (docs/API.md §7.7–§7.10).
 *
 * Paths mirror `apps/api/src/routes/{conversations,messages,users,files}.ts`
 * exactly, including the two places the API deliberately does NOT return the
 * entity (`POST /conversations` returns an id; `POST …/read` returns `{ok}`).
 */
export const httpChatApi: ChatApi = {
  /** `GET /me` returns identity + tenant + permissions; chat only needs the user. */
  me: () => apiFetch<{ user: User }>('/me').then((payload) => payload.user satisfies UserRef),

  /** The API answers `{conversations, nextCursor}`; the UI wants a `Page`. */
  listConversations: (filters, cursor) =>
    apiFetch<{ conversations: Conversation[]; nextCursor: Cursor | null }>(
      `/conversations${query({
        filter: filters.filter,
        kinds: filters.kinds?.join(','),
        q: filters.query,
        teamId: filters.teamId,
        cursor,
      })}`,
    ).then(
      (page): Page<Conversation> => ({
        items: page.conversations,
        pageInfo: {
          hasNextPage: page.nextCursor !== null,
          hasPreviousPage: cursor !== undefined,
          startCursor: cursor ?? null,
          endCursor: page.nextCursor,
        },
      }),
    ),

  getConversation: (conversationId) => apiFetch<Conversation>(conv(conversationId)),

  /** The API answers with an id (a DM may already exist); re-read the entity. */
  createConversation: async (body) => {
    const created = await apiFetch<{ conversationId: ConversationId }>('/conversations', {
      method: 'POST',
      body,
      ...(body.firstMessage?.clientMsgId === undefined
        ? {}
        : { idempotencyKey: body.firstMessage.clientMsgId }),
    });
    return apiFetch<Conversation>(conv(created.conversationId));
  },

  /* The PATCH answers `{ ok: true }`, not the conversation - so callers
     "patched" their cache with nothing, and a mute, pin or archive made from
     the header showed no change until a reload. Read it back, as
     `resolveRequest` does. */
  updateConversation: async (conversationId, body) => {
    await apiFetch<{ ok: true }>(conv(conversationId), { method: 'PATCH', body });
    return apiFetch<Conversation>(conv(conversationId));
  },

  /* EVERYBODY, as the contract says - not the first page. The route pages
     this roster (50 unless asked), and the `@` picker and send-time mention
     matching both read it: in a bigger group, anyone sorted past page one
     could not be picked, and their name typed by hand stayed plain text - no
     mention, no alert. */
  listMembers: async (conversationId) => {
    const members: ConversationMemberRef[] = [];
    let cursor: Cursor | null = null;
    for (let page = 0; page < MEMBER_PAGES; page += 1) {
      const payload: { members?: ConversationMemberRef[]; nextCursor?: Cursor | null } =
        await apiFetch(
          `${conv(conversationId)}/members${query({ limit: MEMBER_PAGE_SIZE, cursor })}`,
        );
      members.push(...(payload.members ?? []));
      cursor = payload.nextCursor ?? null;
      if (cursor === null) break;
    }
    return members;
  },

  /** The server's `{ added }` — narrower than the request whenever somebody is
   *  already in the room or is not a person. Reporting the request back would
   *  be the same lie as "Ringing them now." over `{ added: 0 }`. */
  addMembers: (conversationId, userIds) =>
    apiFetch<{ added: number }>(`${conv(conversationId)}/members`, {
      method: 'POST',
      body: { userIds: [...userIds] },
    }).then((payload) => payload.added ?? 0),

  /** Leaving is this, with your own id — see the contract. */
  removeMember: (conversationId, userId) =>
    apiFetch<{ ok: true }>(`${conv(conversationId)}/members/${userId}`, {
      method: 'DELETE',
    }).then(() => undefined),

  resolveRequest: async (conversationId, action) => {
    await apiFetch<{ ok: true }>(`${conv(conversationId)}/request`, {
      method: 'POST',
      body: { action },
    });
    return apiFetch<Conversation>(conv(conversationId));
  },

  markRead: (conversationId, body) =>
    apiFetch<void>(`${conv(conversationId)}/read`, { method: 'POST', body }).then(() => undefined),

  markUnread: (conversationId, body) =>
    apiFetch(`${conv(conversationId)}/unread`, { method: 'POST', body }),

  /** The WS `typing` frame is the real path; this keeps the contract total. */
  sendTyping: () => Promise.resolve(),

  /**
   * A dedicated sub-resource rather than `PATCH /conversations/:id`, because a
   * draft is written on almost every keystroke burst: it must never contend
   * with, or accidentally clobber, the pin/mute/archive flags in the same row.
   */
  saveDraft: (conversationId, draft) =>
    draft === null
      ? apiFetch<{ draft: MessageDraft | null }>(`${conv(conversationId)}/draft`, {
          method: 'DELETE',
        })
      : apiFetch<{ draft: MessageDraft | null }>(`${conv(conversationId)}/draft`, {
          method: 'PUT',
          body: draft,
        }),

  renameConversation: (conversationId, title) =>
    apiFetch<void>(`${conv(conversationId)}/rename`, { method: 'POST', body: { title } }).then(
      () => undefined,
    ),

  getSelfConversation: () => apiFetch<Conversation>('/conversations/self'),

  getTimeline: (conversationId, request) =>
    apiFetch<TimelineResponse>(
      `${conv(conversationId)}/messages${query({
        before: request.before,
        after: request.after,
        around: request.around,
        limit: request.limit,
        threadRootId: request.threadRootId,
        includeDeleted: request.includeDeleted,
      })}`,
    ),

  sendMessage: (conversationId, body) =>
    apiFetch<Message>(`${conv(conversationId)}/messages`, {
      method: 'POST',
      body,
      idempotencyKey: body.clientMsgId,
    }),

  editMessage: (conversationId, messageId, body) =>
    apiFetch<Message>(`${conv(conversationId)}/messages/${messageId}`, { method: 'PATCH', body }),

  deleteMessage: (conversationId, messageId, mode = 'soft') =>
    apiFetch<void>(`${conv(conversationId)}/messages/${messageId}${query({ mode })}`, {
      method: 'DELETE',
    }).then(() => undefined),

  forwardMessage: (conversationId, messageId, body) =>
    apiFetch<{ messages: Message[] }>(`${conv(conversationId)}/messages/${messageId}/forward`, {
      method: 'POST',
      body,
    }).then((payload) => payload.messages ?? []),

  pinMessage: (conversationId, messageId, pinned) =>
    apiFetch<void>(`${conv(conversationId)}/messages/${messageId}/pin`, {
      method: 'POST',
      body: { pinned },
    }).then(() => undefined),

  /** Threads are a filtered timeline, not a separate collection. */
  getThread: (conversationId, rootMessageId, request) =>
    apiFetch<TimelineResponse>(
      `${conv(conversationId)}/messages${query({
        threadRootId: rootMessageId,
        before: request.before,
        limit: request.limit,
      })}`,
    ),

  listPinned: (conversationId) =>
    apiFetch<{ messages: Message[] }>(`${conv(conversationId)}/messages/pinned`).then(
      (payload) => payload.messages ?? [],
    ),

  addReaction: (conversationId, messageId, value) =>
    apiFetch<{ reactions: Reaction[] }>(
      `${conv(conversationId)}/messages/${messageId}/reactions/${emoji(value)}`,
      { method: 'PUT' },
    ).then((payload) => payload.reactions ?? []),

  removeReaction: (conversationId, messageId, value) =>
    apiFetch<{ reactions: Reaction[] }>(
      `${conv(conversationId)}/messages/${messageId}/reactions/${emoji(value)}`,
      { method: 'DELETE' },
    ).then((payload) => payload.reactions ?? []),

  saveMessage: (conversationId, messageId, note) =>
    apiFetch<void>(`${conv(conversationId)}/messages/${messageId}/save`, {
      method: 'PUT',
      body: note === undefined ? {} : { note },
    }).then(() => undefined),

  unsaveMessage: (conversationId, messageId) =>
    apiFetch<void>(`${conv(conversationId)}/messages/${messageId}/save`, {
      method: 'DELETE',
    }).then(() => undefined),

  listSavedMessages: (cursor) =>
    apiFetch<{ saved: SavedMessage[]; nextCursor: Cursor | null }>(
      `/me/saved-messages${query({ cursor })}`,
    ).then((payload) => toPage(payload.saved ?? [], payload.nextCursor, cursor)),

  setReminder: (conversationId, messageId, body) =>
    apiFetch<{ id: Uuid; remindAt: string }>(
      `${conv(conversationId)}/messages/${messageId}/reminders`,
      { method: 'POST', body },
    ),

  clearReminder: (conversationId, messageId) =>
    apiFetch<void>(`${conv(conversationId)}/messages/${messageId}/reminders`, {
      method: 'DELETE',
    }).then(() => undefined),

  listReminders: (cursor) =>
    apiFetch<{ reminders: MessageReminder[]; nextCursor: Cursor | null }>(
      `/me/reminders${query({ cursor })}`,
    ).then((payload) => toPage(payload.reminders ?? [], payload.nextCursor, cursor)),

  getPreferences: () => apiFetch<UserPreferences>('/me/preferences'),

  updatePreferences: (patch) =>
    apiFetch<UserPreferences>('/me/preferences', { method: 'PATCH', body: patch }),

  /**
   * Send-later. These two were `Promise.resolve([])` and a no-op, which is why
   * the "N scheduled" strip never appeared and Cancel did nothing — the API had
   * no route to call. Both now hit the real queue.
   */
  listScheduled: (conversationId) =>
    apiFetch<{ messages: Message[] }>(`${conv(conversationId)}/scheduled`).then(
      (payload) => payload.messages ?? [],
    ),

  cancelScheduled: (conversationId, messageId) =>
    apiFetch<void>(`${conv(conversationId)}/scheduled/${messageId}`, {
      method: 'DELETE',
    }).then(() => undefined),

  searchUsers: (search, limit = 20) =>
    apiFetch<{ users: UserRef[] }>(`/users/search${query({ q: search, limit })}`).then(
      (payload) => payload.users ?? [],
    ),

  createUpload: (body) => apiFetch('/files/uploads', { method: 'POST', body }),

  refreshUploadParts: (sessionId, partNumbers) =>
    apiFetch(`/files/uploads/${sessionId}/parts`, { method: 'POST', body: { partNumbers } }),

  completeUpload: (sessionId, body) =>
    apiFetch(`/files/uploads/${sessionId}/complete`, { method: 'POST', body }),

  abortUpload: (sessionId) =>
    apiFetch<void>(`/files/uploads/${sessionId}`, { method: 'DELETE' }).then(() => undefined),
};
